import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useNavigationStore } from '@/stores/navigation';
import { useCvFeature } from '@/composables/useCvFeature';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
    },
    {
      path: '/lokasi/:kota',
      name: 'lokasi',
      component: () => import('../views/HomeView.vue'),
    },
    {
      path: '/jobs/:slug',
      name: 'job-detail',
      component: () => import('../views/JobDetailView.vue'),
    },
    {
      path: '/bookmarks',
      name: 'bookmarks',
      component: () => import('../views/BookmarkView.vue'),
    },
    {
      path: '/history',
      name: 'history',
      component: () => import('../views/HistoryView.vue'),
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import('../views/FaqView.vue'),
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('../views/ContactView.vue'),
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/TermsView.vue'),
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import('../views/PrivacyPolicyView.vue'),
    },
    {
      path: '/post-job',
      name: 'post-job',
      component: () => import('../views/PostJobOrderView.vue'),
    },
    {
      path: '/post-job/form',
      name: 'post-job-form',
      component: () => import('../views/PostJobFormView.vue'),
    },
    {
      path: '/payment/:referenceId',
      name: 'payment',
      component: () => import('../views/PaymentView.vue'),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/auth/LoginView.vue'),
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('../views/auth/LoginView.vue'),
    },
    {
      path: '/cv/coming-soon',
      name: 'cv-coming-soon',
      component: () => import('../views/cv/CvComingSoonView.vue'),
    },
    {
      path: '/cv',
      name: 'cv',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvDashboardView.vue'),
    },
    {
      path: '/cv/create',
      name: 'cv-create',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvCreateView.vue'),
    },
    {
      path: '/cv/:id/edit',
      name: 'cv-edit',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvDashboardView.vue'),
    },
    {
      path: '/cv/:id/preview',
      name: 'cv-preview',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvPreviewView.vue'),
    },
    {
      path: '/cv/:id/download',
      name: 'cv-download',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvPreviewView.vue'),
    },
    {
      path: '/cv/packages',
      name: 'cv-packages',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvPackageView.vue'),
    },
    {
      path: '/cv/purchase/:id',
      name: 'cv-purchase',
      meta: { requiresAuth: true },
      component: () => import('../views/cv/CvPurchaseView.vue'),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // Return false to prevent Vue Router from handling scroll.
    // HomeView uses keep-alive + onActivated to restore scroll manually.
    if ((to.name === 'home' || to.name === 'lokasi') && from.name === 'job-detail') {
      return false;
    }
    if (savedPosition) {
      return savedPosition;
    }
    if (to.name === from.name && JSON.stringify(to.query) !== JSON.stringify(from.query)) {
      return false;
    }
    return { top: 0 };
  },
});

router.afterEach((to, from) => {
  if (from.name && to.name !== from.name) {
    const navStore = useNavigationStore();
    navStore.markNavigated();
  }
});

router.beforeEach(async (to, from, next) => {
  if (from.name === 'home' || from.name === 'lokasi') {
    const navigationStore = useNavigationStore();
    navigationStore.saveScroll(window.scrollY);
    navigationStore.saveQuery(JSON.stringify(from.query));
  }

  if ((to.name === 'home' || to.name === 'lokasi') &&
      from.name !== 'home' && from.name !== 'lokasi') {
    const navigationStore = useNavigationStore();
    navigationStore.setReturningToHome(true);
  }

  const auth = useAuthStore();

  if (to.name === 'auth-callback') {
    await auth.handleCallback();
    if (auth.isAuthenticated) {
      const { checkCvFeature } = useCvFeature();
      next({ name: (await checkCvFeature()) ? 'cv' : 'home' });
    } else {
      next({ name: 'login' });
    }
    return;
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    await auth.fetchUser();
    if (!auth.isAuthenticated) {
      next({ name: 'login' });
      return;
    }
  }

  if (to.name === 'login') {
    const { checkCvFeature } = useCvFeature();
    if (!(await checkCvFeature())) {
      next({ name: 'home' });
      return;
    }
    if (auth.isAuthenticated) {
      next({ name: 'cv' });
      return;
    }
  }

  if (to.path.startsWith('/cv') && to.name !== 'cv-coming-soon') {
    const { checkCvFeature } = useCvFeature();
    if (!(await checkCvFeature())) {
      next({ name: 'home' });
      return;
    }
  }

  next();
});

export default router;
